import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import api from "../../utils/api";
import {
  PortalReview,
  PortalReviewStatus,
} from "../../types/PortalReview";

const fail = (err: unknown, fallback: string) => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.message || fallback;
  }
  return fallback;
};

interface PortalReviewState {
  items: PortalReview[];
  pendingCount: number;
  loading: boolean;
  submitting: boolean;
  updatingId: number | null;
  error: string | null;
}

const initialState: PortalReviewState = {
  items: [],
  pendingCount: 0,
  loading: false,
  submitting: false,
  updatingId: null,
  error: null,
};

export const fetchPortalReviews = createAsyncThunk(
  "portalReviews/list",
  async (
    params: { status?: PortalReviewStatus | ""; emp_id?: number | null } | undefined,
    { rejectWithValue },
  ) => {
    try {
      const res = await api.get("/allocation-tool/portal-reviews", {
        params: {
          status: params?.status || undefined,
          emp_id: params?.emp_id || undefined,
        },
      });
      return (res.data.data || []) as PortalReview[];
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load feedback"));
    }
  },
);

export const fetchPendingReviewCount = createAsyncThunk(
  "portalReviews/pendingCount",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/allocation-tool/portal-reviews", {
        params: { status: "pending" },
      });
      return ((res.data.data || []) as PortalReview[]).length;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to load pending feedback"));
    }
  },
);

export const submitPortalReview = createAsyncThunk(
  "portalReviews/submit",
  async (
    payload: {
      title?: string;
      message: string;
      screenshots: File[];
      videos: File[];
    },
    { rejectWithValue },
  ) => {
    try {
      const form = new FormData();
      if (payload.title) form.append("title", payload.title);
      form.append("message", payload.message);
      payload.screenshots.forEach((file) => form.append("screenshots", file));
      payload.videos.forEach((file) => form.append("videos", file));

      const res = await api.post("/allocation-tool/portal-reviews", form);
      return res.data.data as PortalReview;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to submit feedback"));
    }
  },
);

export const updatePortalReviewStatus = createAsyncThunk(
  "portalReviews/status",
  async (
    payload: {
      reviewId: number;
      status: PortalReviewStatus;
      admin_note?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.patch(
        `/allocation-tool/portal-reviews/${payload.reviewId}/status`,
        {
          status: payload.status,
          admin_note: payload.admin_note,
        },
      );
      return res.data.data as PortalReview;
    } catch (err) {
      return rejectWithValue(fail(err, "Failed to update feedback"));
    }
  },
);

const portalReviewSlice = createSlice({
  name: "portalReviews",
  initialState,
  reducers: {
    clearPortalReviewError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortalReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPortalReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        const requestedStatus = action.meta.arg?.status;
        if (!requestedStatus) {
          state.pendingCount = action.payload.filter(
            (item) => item.status === "pending",
          ).length;
        } else if (requestedStatus === "pending") {
          state.pendingCount = action.payload.length;
        }
      })
      .addCase(fetchPortalReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = String(action.payload || "Failed to load feedback");
      })
      .addCase(fetchPendingReviewCount.fulfilled, (state, action) => {
        state.pendingCount = action.payload;
      })
      .addCase(submitPortalReview.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(submitPortalReview.fulfilled, (state, action) => {
        state.submitting = false;
        state.items = [action.payload, ...state.items];
        if (action.payload.status === "pending") {
          state.pendingCount += 1;
        }
      })
      .addCase(submitPortalReview.rejected, (state, action) => {
        state.submitting = false;
        state.error = String(action.payload || "Failed to submit feedback");
      })
      .addCase(updatePortalReviewStatus.pending, (state, action) => {
        state.updatingId = action.meta.arg.reviewId;
        state.error = null;
      })
      .addCase(updatePortalReviewStatus.fulfilled, (state, action) => {
        state.updatingId = null;
        const previous = state.items.find(
          (item) => item.review_id === action.payload.review_id,
        );
        const wasPending = previous?.status === "pending";
        const isPending = action.payload.status === "pending";
        if (wasPending && !isPending) {
          state.pendingCount = Math.max(0, state.pendingCount - 1);
        } else if (!wasPending && isPending) {
          state.pendingCount += 1;
        }
        state.items = state.items.map((item) =>
          item.review_id === action.payload.review_id ? action.payload : item,
        );
      })
      .addCase(updatePortalReviewStatus.rejected, (state, action) => {
        state.updatingId = null;
        state.error = String(action.payload || "Failed to update feedback");
      });
  },
});

export const { clearPortalReviewError } = portalReviewSlice.actions;
export default portalReviewSlice.reducer;
